import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@quebon/ui/components/button'
import {
  createExplainVideoReservation, deleteExplainVideo, ExplainVideoError, getExplainVideo, getExplainVideoCapability,
  listExplainVideos, reserveExplainVideo, resolveVideoContentType, retryExplainVideo, uploadExplainVideoParts,
  type ExplainVideoCapability, type ExplainVideoReservation, type ExplainVideoView,
} from '@/api/lcExplainVideos'
import { ExplainVideoCapture } from './ExplainVideoCapture'
import { LcEvidenceVideoPlayer } from '@/components/common/LcEvidenceVideoPlayer'
import { createVideoFileIdentity, matchesVideoFileIdentity, restoreCapturedUploadFile, type VideoFileIdentity } from './videoUploadIdentity'

interface StoredUpload {
  session: ExplainVideoReservation
  file: VideoFileIdentity | null
  confirmed?: boolean
}

function storageKey(token: string): string {
  // Avoid retaining the QR token in storage keys. Upload tokens remain scoped to one reservation.
  let hash = 2166136261
  for (let index = 0; index < token.length; index += 1) hash = Math.imul(hash ^ token.charCodeAt(index), 16777619)
  return `lc-explain-video:v1:${(hash >>> 0).toString(36)}`
}

function readUploads(token: string): StoredUpload[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(storageKey(token)) ?? '[]')
    if (!Array.isArray(parsed)) return []
    return parsed.filter((item: unknown): item is StoredUpload => {
      if (typeof item !== 'object' || item === null || !('session' in item)) return false
      const session = item.session
      return typeof session === 'object' && session !== null
        && 'id' in session && typeof session.id === 'string'
        && 'uploadToken' in session && typeof session.uploadToken === 'string'
        && 'expiresAt' in session && typeof session.expiresAt === 'string' && Date.parse(session.expiresAt) > Date.now()
        && 'partSize' in session && typeof session.partSize === 'number' && session.partSize > 0
        && 'maxBytes' in session && typeof session.maxBytes === 'number' && session.maxBytes > 0
    }).slice(0, 3)
  } catch {
    return [] // Corrupt/blocked storage must not prevent the existing QR photo flow.
  }
}

export function getRecoverableVideoCapability(token: string): ExplainVideoCapability | null {
  const uploads = readUploads(token)
  if (uploads.length === 0) return null
  return { enabled: true, reason: 'RECOVERING_UPLOAD', maxVideos: 3, currentCount: uploads.length, maxBytes: uploads[0].session.maxBytes, partSize: uploads[0].session.partSize }
}

export function matchesUploadFile(saved: StoredUpload['file'], file: File): Promise<boolean> {
  return matchesVideoFileIdentity(saved, file)
}

export function isUnavailableVideoSession(failure: unknown): boolean {
  return failure instanceof ExplainVideoError && (
    [403, 404, 410].includes(failure.status)
    || ['VIDEO_SESSION_EXPIRED', 'VIDEO_INVALID_STATE', 'INVALID_TOKEN'].includes(failure.code)
  )
}

interface Props {
  token: string
  active: boolean
  initialCapability: ExplainVideoCapability
  variant?: 'page' | 'embedded'
  disabled?: boolean
  onPendingChange?: (pending: boolean) => boolean | void
  onVideosChange?: () => void
}

export function ExplainVideoPanel({ token, active, initialCapability, variant = 'page', disabled = false, onPendingChange, onVideosChange }: Props) {
  const { t } = useTranslation('video')
  const [capability, setCapability] = useState(initialCapability)
  const [videos, setVideos] = useState<ExplainVideoView[]>([])
  const [stored, setStored] = useState(() => readUploads(token))
  const [session, setSession] = useState<ExplainVideoReservation | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewFailed, setPreviewFailed] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [recording, setRecording] = useState(false)
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [storageFailed, setStorageFailed] = useState(false)
  const [needsReservation, setNeedsReservation] = useState(false)
  const [selectionReady, setSelectionReady] = useState<{ kind: 'camera' | 'gallery'; until: number } | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const dimensionsRef = useRef<{ width: number; height: number }>()
  const pickerRef = useRef<HTMLInputElement>(null)
  const pickerCancelRef = useRef<() => void>(() => undefined)
  const selectedSessionRef = useRef<ExplainVideoReservation | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const storedRef = useRef(stored)
  const liveRef = useRef({ session, file, recording })
  const invalidSessionsRef = useRef(new Set<string>())
  const fileIdentityRef = useRef<VideoFileIdentity | null>(null)
  const identityGenerationRef = useRef(0)
  const pendingChangeRef = useRef(onPendingChange)
  const videosChangeRef = useRef(onVideosChange)
  const visibleSignatureRef = useRef('')
  pendingChangeRef.current = onPendingChange
  videosChangeRef.current = onVideosChange
  storedRef.current = stored
  liveRef.current = { session, file, recording }

  const pending = busy || uploading || capturing || recording || pickerOpen || Boolean(session) || Boolean(file)
  const inactiveEmbedded = variant === 'embedded' && !active
  useEffect(() => { pendingChangeRef.current?.(pending) }, [pending])
  useEffect(() => () => { pendingChangeRef.current?.(false) }, [])

  useEffect(() => {
    const signature = videos.filter(video => ['PROCESSING', 'READY', 'FAILED'].includes(video.status))
      .map(video => `${video.id}:${video.status}:${video.version}`).sort().join('|')
    if (signature === visibleSignatureRef.current) return
    visibleSignatureRef.current = signature
    videosChangeRef.current?.()
  }, [videos])

  useEffect(() => {
    if (variant !== 'embedded' || active) return
    // A hidden attachment tab must never keep using the camera or sending a file.
    setCapturing(false)
    abortRef.current?.abort()
  }, [active, variant])

  const remember = useCallback((next: ExplainVideoReservation, pickedFile: File | null, confirmed = true) => {
    setStored(current => [...current.filter(item => item.session.id !== next.id), {
      session: next,
      file: pickedFile ? fileIdentityRef.current?.name === pickedFile.name && fileIdentityRef.current.size === pickedFile.size ? fileIdentityRef.current : { name: pickedFile.name, size: pickedFile.size, lastModified: pickedFile.lastModified } : null,
      confirmed,
    }])
  }, [])
  const forget = useCallback((id: string) => {
    storedRef.current = storedRef.current.filter(item => item.session.id !== id)
    setStored(current => current.filter(item => item.session.id !== id))
  }, [])
  const expireSession = useCallback((id: string) => {
    invalidSessionsRef.current.add(id)
    forget(id)
    setVideos(current => current.filter(video => video.id !== id))
    if (liveRef.current.session?.id !== id) return
    setSelectionReady(null)
    if (liveRef.current.file || liveRef.current.recording) {
      setNeedsReservation(true)
      setError(t('sessionRecovery'))
    } else {
      setSession(null)
      setCapturing(false)
      setError(t('sessionExpiredBeforeCapture'))
    }
  }, [forget, t])

  useEffect(() => {
    try { localStorage.setItem(storageKey(token), JSON.stringify(stored)); setStorageFailed(false) }
    catch { setStorageFailed(true) }
  }, [stored, token])

  const acceptVideo = useCallback((video: ExplainVideoView) => {
    if (invalidSessionsRef.current.has(video.id) && video.status !== 'DELETED' && video.status !== 'EXPIRED') return
    setVideos(current => video.status === 'DELETED' || video.status === 'EXPIRED' ? current.filter(item => item.id !== video.id) : [...current.filter(item => item.id !== video.id), video])
    if (video.status === 'DELETED' || video.status === 'EXPIRED') expireSession(video.id)
    else if (video.expiresAt) setStored(current => {
      if (!current.some(item => item.session.id === video.id && item.session.expiresAt !== video.expiresAt)) return current
      return current.map(item => item.session.id === video.id ? { ...item, session: { ...item.session, expiresAt: video.expiresAt ?? item.session.expiresAt } } : item)
    })
  }, [expireSession])

  const refresh = useCallback(async () => {
    const results = await Promise.allSettled([
      listExplainVideos(token),
      getExplainVideoCapability(token),
      ...storedRef.current.map(async item => {
        if (item.confirmed === false) return
        try { acceptVideo(await getExplainVideo(item.session)) }
        catch (failure) {
          if (isUnavailableVideoSession(failure)) expireSession(item.session.id)
        }
      }),
    ])
    const [list, cap] = results
    if (list.status === 'fulfilled') {
      // Preserve independently authenticated sessions when an expired QR can no longer list the activity.
      setVideos(list.value)
    }
    if (cap.status === 'fulfilled') setCapability(cap.value)
  }, [acceptVideo, expireSession, token])

  useEffect(() => {
    void refresh()
    const timer = window.setInterval(() => void refresh(), 5000)
    return () => { window.clearInterval(timer); abortRef.current?.abort() }
  }, [refresh])

  useEffect(() => {
    if (!file) { setPreviewUrl(null); return }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
    setPreviewFailed(false)
    return () => URL.revokeObjectURL(url)
  }, [file])

  useEffect(() => {
    if (!file && !recording && !uploading) return
    const beforeUnload = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = '' }
    window.addEventListener('beforeunload', beforeUnload)
    return () => window.removeEventListener('beforeunload', beforeUnload)
  }, [file, recording, uploading])

  useEffect(() => {
    const input = pickerRef.current
    const cancel = () => pickerCancelRef.current()
    input?.addEventListener('cancel', cancel)
    return () => input?.removeEventListener('cancel', cancel)
  }, [])

  const describeFailure = (failure: unknown) => {
    if (failure instanceof ExplainVideoError) {
      if (failure.status === 409 || failure.code.includes('LIMIT')) return t('limitReached', { count: capability.maxVideos })
      if (failure.status === 403 || failure.status === 410 || failure.code.includes('EXPIRED') || failure.code === 'INVALID_TOKEN') return t('sessionExpired')
      if (failure.code.includes('TOO_LARGE')) return t('fileTooLarge', { size: Math.round(capability.maxBytes / 1024 ** 3) })
      if (failure.code === 'VIDEO_CAPTURE_UNAVAILABLE') return t('secureContextRequired')
    }
    return t('requestFailed')
  }

  const prepare = async (): Promise<ExplainVideoReservation | null> => {
    if (disabled || inactiveEmbedded || busy || session || file) return null
    setBusy(true)
    setError(null)
    try {
      // Atomic server reservation is the authoritative limit check, before camera or picker opens.
      const proposed = storedRef.current.find(item => item.confirmed === false)?.session ?? createExplainVideoReservation(capability)
      const pending: StoredUpload[] = [...storedRef.current.filter(item => item.session.id !== proposed.id), { session: proposed, file: null, confirmed: false }]
      // Persist the idempotency credentials before dispatch, including when the response is lost.
      try { localStorage.setItem(storageKey(token), JSON.stringify(pending)) }
      catch { setStorageFailed(true) }
      storedRef.current = pending
      setStored(pending)
      const reserved = await reserveExplainVideo(token, proposed)
      setSession(reserved)
      setSelectionReady(null)
      setNeedsReservation(false)
      remember(reserved, null)
      await refresh()
      return reserved
    } catch (failure) { setError(describeFailure(failure)); await refresh(); return null }
    finally { setBusy(false) }
  }

  const discard = async (target: ExplainVideoReservation) => {
    if (busy) return
    abortRef.current?.abort()
    identityGenerationRef.current += 1
    setBusy(true)
    try {
      try { if (!invalidSessionsRef.current.has(target.id)) await deleteExplainVideo(target.id, token, target) }
      catch (failure) {
        const unconfirmed = storedRef.current.some(item => item.session.id === target.id && item.confirmed === false)
        if (!(unconfirmed && failure instanceof ExplainVideoError && failure.status === 404)) throw failure
      }
      invalidSessionsRef.current.add(target.id)
      forget(target.id)
      setVideos(current => current.filter(video => video.id !== target.id))
      if (session?.id === target.id) { liveRef.current = { session: null, file: null, recording: false }; fileIdentityRef.current = null; setSession(null); setFile(null); setCapturing(false); setSelectionReady(null); setNeedsReservation(false); setProgress(0) }
      await refresh()
    } catch (failure) { setError(describeFailure(failure)) }
    finally { setBusy(false) }
  }

  const chooseFile = async (picked: File, target: ExplainVideoReservation, captured = false, interrupted = false, capacityReached = false, restoredIdentity?: VideoFileIdentity) => {
    if (!resolveVideoContentType(picked) || picked.size === 0 || (!captured && picked.size > target.maxBytes)) {
      await discard(target)
      setError(picked.size > target.maxBytes ? t('fileTooLarge', { size: Math.round(target.maxBytes / 1024 ** 3) }) : t('unsupportedType'))
      return
    }
    const generation = ++identityGenerationRef.current
    let identity: VideoFileIdentity
    try { identity = restoredIdentity ?? await createVideoFileIdentity(picked, captured) }
    catch {
      // Keep the source even if fingerprinting is unavailable; legacy metadata still uses strict mtime.
      identity = { name: picked.name, size: picked.size, lastModified: picked.lastModified }
    }
    if (generation !== identityGenerationRef.current) return
    fileIdentityRef.current = identity
    setSession(target)
    setNeedsReservation(invalidSessionsRef.current.has(target.id))
    dimensionsRef.current = undefined
    setFile(picked)
    setCapturing(false)
    setProgress(0)
    setError(picked.size > target.maxBytes ? t('capturedTooLarge') : invalidSessionsRef.current.has(target.id) ? t('sessionRecovery') : capacityReached ? t('recordingCapacity') : interrupted ? t('recordingInterrupted') : null)
    if (!invalidSessionsRef.current.has(target.id)) remember(target, picked)
  }

  const onPick = async (event: ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files?.[0]
    event.target.value = ''
    const target = selectedSessionRef.current
    selectedSessionRef.current = null
    setPickerOpen(false)
    if (!target) return
    if (!picked) { if (!storedRef.current.find(item => item.session.id === target.id)?.file) void discard(target); return }
    const previous = stored.find(item => item.session.id === target.id)
    if (previous?.file) {
      try { if (!await matchesUploadFile(previous.file, picked)) { setError(t('differentFile')); return } }
      catch { setError(t('differentFile')); return }
    }
    const uploadFile = previous?.file ? restoreCapturedUploadFile(previous.file, picked) : picked
    void chooseFile(uploadFile, target, false, false, false, previous?.file ? { ...previous.file, lastModified: uploadFile.lastModified } : undefined)
  }

  pickerCancelRef.current = () => {
    const target = selectedSessionRef.current
    selectedSessionRef.current = null
    setPickerOpen(false)
    // Cancelling a resume picker must retain already-uploaded parts.
    if (target && !storedRef.current.find(item => item.session.id === target.id)?.file) void discard(target)
  }

  const openPicker = (target: ExplainVideoReservation) => {
    selectedSessionRef.current = target
    setPickerOpen(true)
    pickerRef.current?.click()
  }

  const validateForSelection = async (kind: 'camera' | 'gallery', explicitTarget?: ExplainVideoReservation, openImmediately = false) => {
    const target = explicitTarget ?? session
    if (disabled || inactiveEmbedded || !target || busy) return
    if (variant === 'embedded' && pendingChangeRef.current?.(true) === false) return
    setBusy(true)
    setError(null)
    try {
      const latest = await getExplainVideo(target)
      const resumingFile = storedRef.current.some(item => item.session.id === target.id && item.file !== null)
      if (latest.status !== 'RESERVED' && !(resumingFile && kind === 'gallery' && latest.status === 'UPLOADING')) { expireSession(target.id); setError(t('sessionExpiredBeforeCapture')); return }
      acceptVideo(latest)
      setSession(target)
      if (openImmediately && kind === 'camera') {
        setSelectionReady(null)
        setCapturing(true)
        return
      }
      if (openImmediately && navigator.userActivation?.isActive === true) {
        setSelectionReady(null)
        openPicker(target)
        return
      }
      // The next, explicit click opens the camera/picker synchronously; Safari keeps its user gesture.
      setSelectionReady({ kind, until: Date.now() + 15_000 })
    } catch (failure) {
      if (isUnavailableVideoSession(failure)) { expireSession(target.id); setError(t('sessionExpiredBeforeCapture')) }
      else setError(describeFailure(failure))
    } finally { setBusy(false) }
  }

  const openSelection = () => {
    if (disabled || inactiveEmbedded || !session || !selectionReady) return
    if (Date.now() > selectionReady.until || invalidSessionsRef.current.has(session.id)) {
      setSelectionReady(null)
      setError(t('checkAgain'))
      return
    }
    if (selectionReady.kind === 'camera') setCapturing(true)
    else openPicker(session)
    setSelectionReady(null)
  }

  const beginEmbeddedSelection = async (kind: 'camera' | 'gallery') => {
    if (disabled || inactiveEmbedded || busy || uploading || file || capturing) return
    if (!session && capability.currentCount >= capability.maxVideos) { setError(t('limitReached', { count: capability.maxVideos })); return }
    if (pendingChangeRef.current?.(true) === false) return
    const target = session ?? await prepare()
    if (target) await validateForSelection(kind, target, true)
  }

  const upload = async () => {
    if (disabled || inactiveEmbedded || !session || !file || uploading) return
    const controller = new AbortController()
    abortRef.current = controller
    setUploading(true)
    setError(null)
    try {
      let target = session
      if (needsReservation) {
        const proposed = storedRef.current.find(item => item.confirmed === false)?.session ?? createExplainVideoReservation(capability)
        const pending: StoredUpload[] = [...storedRef.current.filter(item => item.session.id !== proposed.id), { session: proposed, file: fileIdentityRef.current ?? { name: file.name, size: file.size, lastModified: file.lastModified }, confirmed: false }]
        try { localStorage.setItem(storageKey(token), JSON.stringify(pending)) }
        catch { setStorageFailed(true) }
        storedRef.current = pending
        setStored(pending)
        setSession(proposed)
        target = await reserveExplainVideo(token, proposed)
        remember(target, file)
        setSession(target)
        setNeedsReservation(false)
      }
      const video = await uploadExplainVideoParts(target, file, controller.signal, setProgress, dimensionsRef.current, updated => { setSession(updated); remember(updated, file) })
      acceptVideo(video)
      setFile(null)
      setSession(null)
      await refresh()
    } catch (failure) {
      if (isUnavailableVideoSession(failure)) expireSession(liveRef.current.session?.id ?? session.id)
      else setError(controller.signal.aborted ? t('uploadPaused') : failure instanceof ExplainVideoError && failure.code.includes('LIMIT') ? describeFailure(failure) : t('uploadFailed'))
    } finally {
      setUploading(false)
      abortRef.current = null
    }
  }

  const deleteUploaded = async (id: string) => {
    if (busy || uploading) return
    setBusy(true)
    setError(null)
    try {
      const saved = stored.find(item => item.session.id === id)
      await deleteExplainVideo(id, token, saved?.session)
      forget(id)
      setVideos(current => current.filter(video => video.id !== id))
      await refresh()
    } catch (failure) { setError(describeFailure(failure)) }
    finally { setBusy(false) }
  }

  const retryConversion = async (saved: StoredUpload) => {
    setBusy(true)
    setError(null)
    try { acceptVideo(await retryExplainVideo(saved.session)) }
    catch (failure) { setError(describeFailure(failure)) }
    finally { setBusy(false) }
  }

  const recoverable = stored.filter(item => item.session.id !== session?.id && !videos.some(video => video.id === item.session.id && ['PROCESSING', 'READY', 'FAILED'].includes(video.status)))
  const visibleVideos = videos.filter(video => ['PROCESSING', 'READY', 'FAILED'].includes(video.status))
  const embeddedLimitReached = !session && capability.currentCount >= capability.maxVideos

  return <div className="space-y-5" hidden={variant === 'embedded' && !active}>
    {active ? <section aria-label={t('addVideo')} className="space-y-3">
      {variant === 'embedded' ? <>
        {!file && !capturing ? <>
          {selectionReady ? <Button type="button" onClick={openSelection} disabled={disabled || busy} className="min-h-11 w-full">{selectionReady.kind === 'camera' ? t('openCamera') : t('openGallery')}</Button> : <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={() => void beginEmbeddedSelection('camera')} disabled={disabled || busy || uploading || embeddedLimitReached} className="min-h-11">{t('capture')}</Button>
            <Button type="button" variant="outline" onClick={() => void beginEmbeddedSelection('gallery')} disabled={disabled || busy || uploading || embeddedLimitReached} className="min-h-11">{t('chooseVideo')}</Button>
          </div>}
          {busy ? <p role="status" className="text-sm text-muted-foreground">{t('checkingLimit')}</p> : null}
          {session ? <Button type="button" variant="outline" onClick={() => void discard(session)} disabled={disabled || busy} className="min-h-11">{t('cancel')}</Button> : null}
        </> : null}
      </> : <>
      <p className="text-sm text-gray-600">{t('limitHint', { count: capability.maxVideos, remaining: Math.max(0, capability.maxVideos - capability.currentCount) })}</p>
      {!session ? <button type="button" onClick={() => void prepare()} disabled={disabled || busy || uploading} className="min-h-12 w-full rounded-xl bg-primary-600 px-3 py-3 font-semibold text-white disabled:bg-gray-300">{busy ? t('checkingLimit') : t('addVideo')}</button> : null}
      {session && !file && !capturing ? <>
        {selectionReady ? <button type="button" onClick={openSelection} disabled={disabled || busy} className="min-h-12 w-full rounded-xl bg-primary-600 px-3 py-3 font-semibold text-white disabled:bg-gray-300">{selectionReady.kind === 'camera' ? t('openCamera') : t('openGallery')}</button> : <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => void validateForSelection('camera')} disabled={disabled || busy} className="min-h-12 rounded-xl bg-primary-600 px-3 py-3 font-semibold text-white disabled:bg-gray-300">{t('capture')}</button>
          <button type="button" onClick={() => void validateForSelection('gallery')} disabled={disabled || busy} className="min-h-12 rounded-xl border border-primary-600 bg-white px-3 py-3 font-semibold text-primary-700 disabled:opacity-50">{t('chooseVideo')}</button>
        </div>}
        <button type="button" onClick={() => void discard(session)} disabled={disabled || busy} className="min-h-11 px-2 text-sm text-gray-600">{t('cancel')}</button>
      </> : null}
      </>}
    </section> : null}
    {(variant === 'page' || active) && capturing && session ? <ExplainVideoCapture maxBytes={session.maxBytes} onFile={(picked, interrupted, capacityReached) => void chooseFile(picked, session, true, interrupted, capacityReached)} onCancel={() => void discard(session)} onRecordingChange={setRecording} onUnavailable={() => { void discard(session).then(() => setError(t('cameraUnavailable'))) }} /> : null}
    <input ref={pickerRef} type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v,.mov,.mp4,.webm,.m4v" className="hidden" aria-label={t('chooseVideo')} onChange={onPick} />
    {error ? <p role="alert" className="text-sm text-red-600">{error}</p> : null}
    {storageFailed ? <p role="status" className="text-sm text-gray-600">{t('storageUnavailable')}</p> : null}
    {file && previewUrl && session ? <section aria-label={t('preview')} className="space-y-3 rounded-xl border border-gray-200 bg-white p-3">
      <h2 className="text-sm font-semibold text-gray-700">{t('preview')}</h2>
      <video src={previewUrl} controls playsInline preload="metadata" hidden={previewFailed} className="max-h-80 w-full rounded-lg bg-black" onError={() => setPreviewFailed(true)} onLoadedMetadata={event => {
        const video = event.currentTarget
        if (video.videoWidth > 0 && video.videoHeight > 0) dimensionsRef.current = { width: video.videoWidth, height: video.videoHeight }
        else setPreviewFailed(true)
      }} onCanPlay={event => { const video = event.currentTarget; setPreviewFailed(video.videoWidth <= 0 || video.videoHeight <= 0) }} aria-label={t('preview')} />
      {previewFailed ? <p role="status" className="text-sm text-gray-600">{t('previewUnavailable')}</p> : null}
      <p className="break-all text-xs text-gray-500">{file.name} · {(file.size / 1024 ** 2).toFixed(1)} MB</p>
      <p className="text-xs text-gray-600">{t('keepPageOpen')}</p>
      <a href={previewUrl} download={file.name} className="inline-block min-h-11 py-2 text-sm text-primary-700 underline">{t('saveOriginal')}</a>
      {uploading || progress > 0 ? <div><progress value={progress} max={file.size} aria-label={t('uploadProgress')} className="w-full" /><p role="status" className="text-sm text-gray-600">{t('progress', { percent: Math.floor(progress / file.size * 100) })}</p></div> : null}
      <div className="flex gap-3">
        <button type="button" disabled={disabled || busy || file.size > session.maxBytes} onClick={uploading ? () => abortRef.current?.abort() : () => void upload()} className="min-h-11 flex-1 rounded-lg bg-primary-600 px-3 py-2 font-semibold text-white disabled:bg-gray-300">{uploading ? t('pauseUpload') : needsReservation ? t('recoverUpload') : progress > 0 ? t('resumeUpload') : t('upload')}</button>
        <button type="button" disabled={disabled || uploading || busy} onClick={() => void discard(session)} className="min-h-11 rounded-lg border border-gray-300 px-3 py-2 disabled:opacity-50">{t('remove')}</button>
      </div>
    </section> : null}
    {recoverable.map(saved => <section key={saved.session.id} className="space-y-2 rounded-xl border border-gray-200 bg-white p-3">
      <p className="text-sm text-gray-600">{saved.file ? t('resumeHint', { name: saved.file.name }) : t('unusedReservation')}</p>
      <div className="flex gap-3">
        {saved.file ? <button type="button" disabled={disabled || Boolean(file) || busy || uploading} onClick={() => void validateForSelection('gallery', saved.session)} className="min-h-11 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300">{t('reselectFile')}</button> : null}
        <button type="button" disabled={disabled || busy || uploading} onClick={() => void discard(saved.session)} className="min-h-11 rounded-lg border border-gray-300 px-3 py-2 text-sm">{t('remove')}</button>
      </div>
    </section>)}
    {variant === 'page' ? <section aria-label={t('uploaded')} className="space-y-3">
      <h2 className="text-sm font-semibold text-gray-700">{t('uploaded')} ({capability.currentCount} / {capability.maxVideos})</h2>
      {visibleVideos.length === 0 ? <p className="text-sm text-gray-400">{t('empty')}</p> : null}
      {visibleVideos.map((video, index) => <div key={video.id} className="space-y-2 rounded-xl border border-gray-200 bg-white p-3">
        {video.status === 'READY' && video.playbackUrl ? <LcEvidenceVideoPlayer url={video.playbackUrl} label={t('videoNumber', { number: index + 1 })} onRefreshUrl={async () => {
          const saved = storedRef.current.find(item => item.session.id === video.id)
          const latest = saved ? await getExplainVideo(saved.session) : (await listExplainVideos(token)).find(item => item.id === video.id)
          if (latest) acceptVideo(latest)
          return latest?.playbackUrl ?? undefined
        }} /> : <p role="status" className="text-sm text-gray-600">{t(video.status === 'FAILED' ? 'processingFailed' : 'processing')}</p>}
        <div className="flex gap-3">
          {video.status === 'FAILED' && stored.some(item => item.session.id === video.id) ? <button type="button" disabled={disabled || busy || uploading} onClick={() => { const saved = stored.find(item => item.session.id === video.id); if (saved) void retryConversion(saved) }} className="min-h-11 rounded-lg border border-primary-600 px-3 py-2 text-sm text-primary-700">{t('retryProcessing')}</button> : null}
          <button type="button" disabled={disabled || busy || uploading} onClick={() => void deleteUploaded(video.id)} aria-label={t('deleteVideo', { number: index + 1 })} className="min-h-11 rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:opacity-50">{t('remove')}</button>
        </div>
      </div>)}
    </section> : null}
  </div>
}
